import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, HelpCircle, Inbox, Loader2, Plus, Trash2, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { mailboxesApi } from '@/api/mailboxes';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorState } from '@/components/ui/error-state';
import { Field } from '@/components/ui/field';
import { Loader } from '@/components/ui/loader';
import { Modal } from '@/components/ui/modal';
import { StatusPill } from '@/components/ui/status-pill';
import { Topbar } from '@/components/ui/topbar';
import { ApiError } from '@/lib/api-client';
import { connectSmtpSchema, type ConnectSmtpInput } from '@/lib/form-schemas';
import { qk } from '@/lib/query-keys';

export function MailboxesPage(): JSX.Element {
  const [connectOpen, setConnectOpen] = useState(false);
  const list = useQuery({
    queryKey: qk.mailboxes.all,
    queryFn: () => mailboxesApi.list(),
  });
  const qc = useQueryClient();
  const del = useMutation({
    mutationFn: (id: string) => mailboxesApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.mailboxes.all }),
  });

  const mailboxes = list.data ?? [];

  return (
    <>
      <Topbar
        title="Mailboxes"
        description={`${mailboxes.length} connected mailbox${mailboxes.length === 1 ? '' : 'es'}`}
        actions={
          <div className="flex gap-2">
            <button
              className="mb-btn-secondary"
              onClick={() => {
                // Kick off the OAuth flow. The API endpoint 302-redirects
                // to Google's consent screen, then back to /mailboxes with
                // ?connected=<email> once the callback finishes.
                window.location.href = '/api/v1/oauth/google/start';
              }}
              title="Connect a Google Workspace mailbox via OAuth"
            >
              <GoogleGlyph className="h-4 w-4" />
              Connect with Google
            </button>
            <button className="mb-btn-primary" onClick={() => setConnectOpen(true)}>
              <Plus className="h-4 w-4" strokeWidth={2} />
              Connect via App Password
            </button>
          </div>
        }
      />

      <OAuthResultBanner />


      <div className="flex-1 overflow-auto p-6">
        {list.isLoading ? (
          <div className="flex justify-center py-10">
            <Loader />
          </div>
        ) : list.isError ? (
          <ErrorState
            title="Couldn't load mailboxes"
            onRetry={() => void list.refetch()}
          />
        ) : mailboxes.length === 0 ? (
          <EmptyState
            icon={Inbox}
            title="No mailboxes connected"
            description="Connect a Google Workspace mailbox in one click, or paste SMTP creds manually."
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {mailboxes.map((m) => (
              <div key={m.id} className="mb-card p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium text-mb-text truncate">
                      {m.display_name || m.email}
                    </div>
                    <div className="text-xs text-mb-text-muted truncate">{m.email}</div>
                    <div className="mt-2 flex items-center gap-2 text-xs">
                      <StatusPill status={m.status} />
                      <span className="text-mb-text-subtle">{m.provider}</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm(`Disconnect ${m.email}?`)) del.mutate(m.id);
                    }}
                    className="text-mb-text-muted hover:text-mb-danger"
                    aria-label="Disconnect mailbox"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <div className="mt-3 text-xs text-mb-text-muted">
                  {m.sends_today}/{m.daily_send_cap} today · {m.sends_this_hour}/
                  {m.hourly_send_cap} this hour
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ConnectSmtpModal open={connectOpen} onClose={() => setConnectOpen(false)} />
    </>
  );
}

/**
 * Google's official "G" glyph — used on the Connect-with-Google button so
 * it reads as "sign in with Google" the way users expect.
 */
function GoogleGlyph({ className }: { className?: string }): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden focusable="false">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.75h3.57c2.08-1.92 3.28-4.74 3.28-8.07z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.75c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.12A6.98 6.98 0 0 1 5.5 12c0-.74.12-1.45.34-2.12V7.04H2.18A10.99 10.99 0 0 0 1 12c0 1.78.43 3.47 1.18 4.96l3.66-2.84z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.65l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.04l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
    </svg>
  );
}

/**
 * Toast-ish banner that surfaces the result of an OAuth round-trip. The
 * callback route redirects back with ?connected=<email> on success or
 * ?oauth_error=<msg> on failure — we read those and show a strip, then
 * clean the query string so a refresh doesn't re-show it.
 */
function OAuthResultBanner(): JSX.Element | null {
  const [message, setMessage] = useState<
    { kind: 'success' | 'error'; text: string } | null
  >(null);

  useEffect(() => {
    const url = new URL(window.location.href);
    const connected = url.searchParams.get('connected');
    const err = url.searchParams.get('oauth_error');
    if (connected) {
      setMessage({ kind: 'success', text: `Connected ${connected}` });
    } else if (err) {
      setMessage({ kind: 'error', text: `OAuth failed: ${err}` });
    }
    if (connected || err) {
      url.searchParams.delete('connected');
      url.searchParams.delete('oauth_error');
      window.history.replaceState(null, '', url.toString());
    }
  }, []);

  if (!message) return null;
  const isOk = message.kind === 'success';
  return (
    <div
      className={
        'mx-6 mt-4 flex items-center gap-2 rounded-md border p-3 text-sm ' +
        (isOk
          ? 'border-mb-success/30 bg-mb-success-soft text-mb-success'
          : 'border-mb-danger/30 bg-mb-danger-soft text-mb-danger')
      }
      role="alert"
    >
      {isOk ? (
        <CheckCircle2 className="h-4 w-4" />
      ) : (
        <XCircle className="h-4 w-4" />
      )}
      {message.text}
      <button
        type="button"
        onClick={() => setMessage(null)}
        className="ml-auto text-xs hover:underline"
      >
        dismiss
      </button>
    </div>
  );
}

function ConnectSmtpModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm<ConnectSmtpInput>({
    resolver: zodResolver(connectSmtpSchema),
    defaultValues: { email: '', password: '' },
    mode: 'onSubmit',
  });

  const connect = useMutation({
    mutationFn: (v: ConnectSmtpInput) =>
      mailboxesApi.connectSmtp({
        email: v.email,
        password: v.password,
        display_name: v.display_name || undefined,
        smtp_host: v.smtp_host || undefined,
        smtp_port: Number.isFinite(v.smtp_port) ? Number(v.smtp_port) : undefined,
        imap_host: v.imap_host || undefined,
        imap_port: Number.isFinite(v.imap_port) ? Number(v.imap_port) : undefined,
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: qk.mailboxes.all });
      form.reset();
      onClose();
    },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setSubmitError(null);
    try {
      await connect.mutateAsync(values);
    } catch (err) {
      setSubmitError(err instanceof ApiError ? err.message : 'Could not connect mailbox');
    }
  });

  return (
    <Modal open={open} onClose={onClose} title="Connect mailbox with app password" size="md">
      <form onSubmit={onSubmit} className="space-y-3" noValidate>
        <Field
          label="Work email"
          hint="Consumer domains (gmail.com, outlook.com) are blocked — use a Workspace/M365 account."
          required
          error={form.formState.errors.email?.message}
        >
          <input
            type="email"
            className="mb-input"
            autoComplete="username"
            placeholder="you@yourcompany.com"
            {...form.register('email')}
          />
        </Field>
        <div>
          <div className="flex items-center gap-1.5 mb-1.5">
            <label className="block text-xs font-medium text-mb-text uppercase tracking-wider">
              App Password <span className="text-mb-danger">*</span>
            </label>
            <div className="group relative">
              <HelpCircle
                className="h-3.5 w-3.5 text-mb-text-muted cursor-help"
                aria-label="How to generate an app password"
              />
              <div
                role="tooltip"
                className="pointer-events-none absolute left-5 top-0 z-50 w-80 rounded-md border border-mb-border bg-mb-surface p-3 text-xs text-mb-text-muted shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <div className="font-semibold text-mb-text mb-1.5">How to generate an app password</div>
                <b>Gmail / Google Workspace:</b>
                <ol className="list-decimal ml-4 mt-1 space-y-0.5">
                  <li>Go to <span className="font-mono">myaccount.google.com</span> → <b>Security</b></li>
                  <li>Enable <b>2-Step Verification</b> if not already on</li>
                  <li>Open <b>App passwords</b> → generate for "Mail"</li>
                  <li>Paste the 16-character code here (spaces optional)</li>
                </ol>
                <b className="block mt-2">Microsoft 365 / Outlook:</b>
                <ol className="list-decimal ml-4 mt-1 space-y-0.5">
                  <li>Go to <span className="font-mono">account.microsoft.com/security</span></li>
                  <li>Advanced security options → App passwords → Create new</li>
                </ol>
                <div className="mt-2 text-[11px]">Your regular login password won't work — you need an app password specifically.</div>
              </div>
            </div>
          </div>
          <input
            type="password"
            className="mb-input w-full"
            autoComplete="new-password"
            placeholder="16-character app password"
            {...form.register('password')}
          />
          {form.formState.errors.password?.message ? (
            <p className="text-xs text-mb-danger mt-1">{form.formState.errors.password.message}</p>
          ) : null}
        </div>
        <Field label="Display name (optional)" error={form.formState.errors.display_name?.message}>
          <input
            type="text"
            className="mb-input"
            placeholder="e.g. Fatima Baloch"
            {...form.register('display_name')}
          />
        </Field>
        <div className="text-xs text-mb-text-muted bg-mb-bg-soft border border-mb-border rounded-md p-2.5">
          SMTP + IMAP host/ports are auto-detected from your email domain — Gmail, MS365, and Google Workspace domains all work out of the box.
        </div>
        {submitError ? (
          <div role="alert" className="rounded-md border border-mb-danger/30 bg-mb-danger-soft p-2 text-xs text-mb-danger">
            {submitError}
          </div>
        ) : null}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="mb-btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className="mb-btn-primary"
            disabled={connect.isPending || form.formState.isSubmitting}
          >
            {connect.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Connect
          </button>
        </div>
      </form>
    </Modal>
  );
}
